import React, { useState } from 'react';
import { TrendingUpIcon, MessageSquareIcon, WorkflowIcon, ZapIcon, SparklesIcon } from 'lucide-react';
import { DemoModal } from './DemoModal';
export function Services() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const consultingServices = [{
    icon: <TrendingUpIcon size={28} className="text-blue-600" data-id="element-324" />,
    title: 'Get more business',
    tag: 'Flagship',
    description: 'Every missed call and slow follow-up is business walking out the door. We plug the leaks first — answering every call, following up in seconds, nurturing the maybes — then help you create new demand.',
    useCases: ['Answer every call', 'Instant lead follow-up', 'Nurture the maybes', 'Outbound & marketing']
  }, {
    icon: <MessageSquareIcon size={28} className="text-blue-600" data-id="element-325" />,
    title: 'Keep your customers happy',
    description: 'Your customers get an instant, accurate answer at 2am — and your team stops fielding the same questions all day. Support and service handled across chat, email, and SMS, without adding headcount.',
    useCases: ['Chat, email & SMS', '24/7 answers', 'Instant resolutions', 'No extra headcount']
  }, {
    icon: <WorkflowIcon size={28} className="text-blue-600" data-id="element-326" />,
    title: 'Run the back office on autopilot',
    description: 'The copy-paste between systems, the invoice chasing, the paperwork — done automatically. We hand the repetitive busywork to AI so your team gets its week back.',
    useCases: ['Workflow automation', 'Finance (AP/AR)', 'Documents & contracts', 'System-to-system']
  }];
  return <section id="services" className="py-20 bg-gray-50" data-id="element-327">
      <div className="container mx-auto px-4" data-id="element-328">
        <div className="text-center mb-16" data-id="element-329">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-330">
            Work that should be getting done — done
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mb-6 rounded-full" data-id="element-331"></div>
          <p className="max-w-2xl mx-auto text-gray-600 text-lg" data-id="element-332">
            Some of it makes you money, some of it saves you money. Either way
            it's work currently done badly, late, or not at all — and AI can
            handle it.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12" data-id="element-333">
          {consultingServices.map((service, index) => <div key={index} className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm flex flex-col" data-id="element-334">
              <div className="flex items-center justify-between mb-5" data-id="element-335">
                <div className="p-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg w-fit border border-purple-100/50" data-id="element-336">
                  {service.icon}
                </div>
                {service.tag && <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-blue-600 text-white" data-id="element-337">
                    {service.tag}
                  </span>}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-338">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm mb-6 flex-grow" data-id="element-339">
                {service.description}
              </p>

              {index === 0 && <button onClick={() => setIsDemoModalOpen(true)} className="mb-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white text-sm font-semibold rounded-lg transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5" data-id="element-340">
                  <ZapIcon size={16} data-id="element-341" />
                  Live Demo
                </button>}

              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100" data-id="element-342">
                {service.useCases.map((tag, tagIndex) => <span key={tagIndex} className="text-xs font-medium px-2.5 py-1 rounded-full border bg-blue-50 text-blue-700 border-blue-100" data-id="element-343">
                    {tag}
                  </span>)}
              </div>
            </div>)}
        </div>

        {/* Agentic AI — cross-cutting premium note */}
        <div className="max-w-3xl mx-auto mb-20 flex items-start gap-4 bg-white border border-purple-100 rounded-xl p-6 shadow-sm" data-id="element-344">
          <div className="shrink-0 p-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-purple-100/50" data-id="element-345">
            <SparklesIcon size={24} className="text-purple-600" data-id="element-346" />
          </div>
          <div data-id="element-347">
            <div className="flex items-center gap-2 mb-1" data-id="element-348">
              <h3 className="text-base font-bold text-gray-900" data-id="element-349">Agentic AI</h3>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100" data-id="element-350">
                Premium
              </span>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-351">
              The deep-end version of any of the above — AI running a whole
              workflow, not one task. Available as a Pro-tier upgrade once the
              first wins are on the board.
            </p>
          </div>
        </div>

        <div className="bg-white p-8 md:p-12 rounded-2xl border border-gray-200 shadow-sm" data-id="element-352">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-353">
            <div className="md:w-1/2" data-id="element-354">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-355">
                How we build it
              </h3>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed" data-id="element-356">
                We don't hand you a generic tool. We learn how you actually run,
                find the two or three spots where AI would pay off fastest, and
                build it in — with measurement from day one so you can see what
                it's producing.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-357">
                <li className="flex items-center gap-3 text-gray-700" data-id="element-358">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shrink-0" data-id="element-359"></div>
                  <span data-id="element-360">Find where it's leaking or grinding first</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700" data-id="element-361">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shrink-0" data-id="element-362"></div>
                  <span data-id="element-363">Built around your business and your systems</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700" data-id="element-364">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shrink-0" data-id="element-365"></div>
                  <span data-id="element-366">
                    Measured from the start — part of our fee can ride on it
                  </span>
                </li>
              </ul>
              <a href="#contact" className="text-blue-600 font-semibold hover:text-blue-700 inline-flex items-center gap-2" data-id="element-367">
                Start with a free audit <span aria-hidden="true" data-id="element-368">&rarr;</span>
              </a>
            </div>
            <div className="md:w-1/2" data-id="element-369">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Strategic consulting session" className="rounded-xl shadow-lg w-full object-cover h-64 md:h-auto" data-id="element-370" />
            </div>
          </div>
        </div>
      </div>

      <DemoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} data-id="element-371" />
    </section>;
}